import { useCallback, useEffect, useRef, useState } from 'react';
import useAsyncEffect from 'use-async-effect';

import { useDeviceChange } from '../../DevicesSettings/hooks';
import { useConferenceState } from '@hooks/conferenceContext';
import { MytaverseLogger } from '@helpers/logger';
import { getCatchErrorMessage } from '@helpers/error';
import { NavigatorPermissionsService } from '@helpers/permissionsService';

import { IDevicePermissionState } from '@interfaces/permissions';
import { AnalyticsType } from '@interfaces/analytics';
import { useMytaverseEvent } from '@core/dashboard/providers';
import MillicastService from '@core/dashboard/components/HomeBottomBar/ShareScreen/helpers';

/**
 * Drives the microphone step: resolves the permission, lists the input devices
 * and keeps a preview stream open on the selected one.
 *
 * @returns The device list, the selected device id with its setter, the
 * permission state, and whether the list is still being resolved.
 */
export const useInitMicrophonesDevices = () => {
  const [initializing, setInitializing] = useState(true);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [permissionState, setPermissionState] =
    useState<IDevicePermissionState | null>(null);
  const trackMicPermissionsChange = useRef(false);
  // Ownership of the preview stream lives in a ref, not in state — see the same
  // comment in the webcam hook: two starts can land in one React batch, and the
  // stream the batch drops is then unreachable by any cleanup (MV-1815).
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const unmountedRef = useRef(false);

  const { activeMicroDeviceId, setActiveMicroDeviceId } = useConferenceState();
  const { trackEventAnalytics } = useMytaverseEvent();

  useDeviceChange(setInitializing);

  const releaseMicStream = useCallback(() => {
    if (mediaStreamRef.current) {
      MillicastService.stopMediaStreamTracks(mediaStreamRef.current);
      mediaStreamRef.current = null;
    }
  }, []);

  const startMicStream = useCallback(
    async (deviceId: string) => {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          deviceId: { exact: deviceId },
        },
        video: false,
      });

      if (unmountedRef.current) {
        MillicastService.stopMediaStreamTracks(mediaStream);
        return;
      }

      releaseMicStream();
      mediaStreamRef.current = mediaStream;
    },
    [releaseMicStream],
  );

  useAsyncEffect(async () => {
    switch (permissionState) {
      case 'prompt': {
        trackMicPermissionsChange.current = true;

        trackEventAnalytics(AnalyticsType.InputDevicePermissionsPrompt, {
          type: 'mic',
        });

        try {
          // Only here to raise the permission prompt — release the microphone
          // straight away, the test stream is opened by startMicStream below.
          const permissionStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false,
          });

          MillicastService.stopMediaStreamTracks(permissionStream);

          setPermissionState('granted');
        } catch (error) {
          setPermissionState('denied');
        }
        break;
      }
      case 'denied': {
        if (trackMicPermissionsChange.current) {
          trackEventAnalytics(AnalyticsType.InputDevicePermissionsDeny, {
            type: 'mic',
          });
          trackMicPermissionsChange.current = false;
        }

        setDevices([]);
        setInitializing(false);
        break;
      }
      case 'granted': {
        if (!initializing) {
          return;
        }

        if (!navigator.mediaDevices?.enumerateDevices) {
          throw new Error('enumerateDevices() not supported.');
        }

        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const microDevices = (devices || []).filter(
            (device) => device.kind === 'audioinput',
          );

          localStorage.setItem('microDevices', JSON.stringify(microDevices));

          const activeDeviceId =
            microDevices.find(
              ({ deviceId }) => deviceId === activeMicroDeviceId,
            )?.deviceId || microDevices[0]?.deviceId;

          setDevices(microDevices);
          if (activeDeviceId) {
            setActiveMicroDeviceId(activeDeviceId);
            await startMicStream(activeDeviceId);
          }
          setInitializing(false);

          if (trackMicPermissionsChange.current) {
            trackEventAnalytics(AnalyticsType.InputDevicePermissionsAccept, {
              type: 'mic',
            });
          }
        } catch (error) {
          throw Error(getCatchErrorMessage(error));
        }
        break;
      }
    }
  }, [
    permissionState,
    activeMicroDeviceId,
    initializing,
    setActiveMicroDeviceId,
    trackEventAnalytics,
    startMicStream,
  ]);

  useAsyncEffect(async () => {
    try {
      const permissionData =
        await NavigatorPermissionsService.checkAndEnsurePermission(
          'microphone',
        );
      setPermissionState(permissionData.state);

      if ('onchange' in permissionData) {
        permissionData.onchange = () => {
          trackMicPermissionsChange.current = true;
          setPermissionState(permissionData.state);
          setInitializing(true);
        };
      }
    } catch (error) {
      setPermissionState('denied');
      MytaverseLogger.error(getCatchErrorMessage(error));
    }
  }, []);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      releaseMicStream();
    };
  }, [releaseMicStream]);

  return {
    initializing,
    devices,
    activeDeviceId: activeMicroDeviceId,
    setActiveDeviceId: setActiveMicroDeviceId,
    permissionState,
  };
};
